import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@journ/ui/accordion'
import { Button } from '@journ/ui/button'
import { Input } from '@journ/ui/input'
import { Label } from '@journ/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@journ/ui/select'
import { DatePicker } from '@/components/date-picker'
import { SubmitButton } from '@journ/ui/submit-button'
import { useState } from 'react'
import { useIntlayer } from 'react-intlayer'
import { useDepartments } from '../hooks/use-departments'
import { z } from '@/libs/zod'
import type { EmploymentType, MemberStatus, TeamMember } from '../domain/member'
import { MemberAvatarField } from './member-avatar-field'

const schema = z.object({
	fullName: z.string().min(1),
	preferredName: z.string().optional(),
	email: z.email(),
	personalEmail: z.string().optional(),
	workPhone: z.string().optional(),
	personalPhone: z.string().optional(),
	position: z.string().optional(),
	departmentId: z.string().optional(),
	employmentType: z.enum(['full_time', 'part_time', 'contractor', 'intern', 'temporary']).optional(),
	startDate: z.string().optional(),
	status: z.enum(['active', 'on_leave', 'departed', 'archived']).optional(),
	location: z.string().optional(),
	timezone: z.string().optional(),
	addressLine1: z.string().optional(),
	addressLine2: z.string().optional(),
	city: z.string().optional(),
	state: z.string().optional(),
	countryCode: z.string().optional(),
	zip: z.string().optional(),
	defaultWorkSchedule: z.string().optional(),
})

type FormValues = z.infer<typeof schema>

type Props = {
	data?: TeamMember
	onSubmit: (values: FormValues & { id?: string }) => Promise<void>
	onCancel: () => void
}

const field = (label: string, children: React.ReactNode, error?: string) => (
	<div className='space-y-1.5'>
		<Label className='text-xs text-878787 font-normal'>{label}</Label>
		{children}
		{error && <p className='text-xs text-destructive'>{error}</p>}
	</div>
)

const EMPLOYMENT_TYPES: EmploymentType[] = ['full_time', 'part_time', 'contractor', 'intern', 'temporary']
const STATUSES: MemberStatus[] = ['active', 'on_leave', 'departed', 'archived']
const NO_DEPARTMENT = '__none__'

const MemberForm = ({ data, onSubmit, onCancel }: Props) => {
	const content = useIntlayer('hr-page')
	const isEdit = !!data
	const { departments } = useDepartments()

	const [values, setValues] = useState({
		fullName: data?.fullName ?? '',
		preferredName: data?.preferredName ?? '',
		email: data?.email ?? '',
		personalEmail: data?.personalEmail ?? '',
		workPhone: data?.workPhone ?? '',
		personalPhone: data?.personalPhone ?? '',
		position: data?.position ?? '',
		departmentId: data?.departmentId ?? '',
		employmentType: data?.employmentType ?? 'full_time',
		startDate: data?.startDate ?? '',
		status: data?.status ?? 'active',
		location: data?.location ?? '',
		timezone: data?.timezone ?? '',
		addressLine1: data?.addressLine1 ?? '',
		addressLine2: data?.addressLine2 ?? '',
		city: data?.city ?? '',
		state: data?.state ?? '',
		countryCode: data?.countryCode ?? '',
		zip: data?.zip ?? '',
		defaultWorkSchedule: data?.defaultWorkSchedule ?? '',
	})
	const [errors, setErrors] = useState<Partial<Record<string, string>>>({})
	const [submitting, setSubmitting] = useState(false)

	const set = (key: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
		setValues(v => ({ ...v, [key]: e.target.value }))

	const setSelect = (key: string) => (value: string) => setValues(v => ({ ...v, [key]: value }))

	const handleSubmit = async (e: React.SubmitEvent<HTMLFormElement>) => {
		e.preventDefault()
		const result = schema.safeParse(values)
		if (!result.success) {
			const flat = z.flattenError(result.error).fieldErrors
			setErrors(Object.fromEntries(Object.entries(flat).map(([k, v]) => [k, v?.[0]])))
			return
		}
		setErrors({})
		setSubmitting(true)
		try {
			await onSubmit({ ...result.data, id: data?.id })
		} finally {
			setSubmitting(false)
		}
	}

	return (
		<form onSubmit={handleSubmit}>
			<div className='h-[calc(100vh-180px)] overflow-auto'>
				{data && <MemberAvatarField memberId={data.id} fullName={data.fullName} avatarUrl={data.avatarUrl} />}
				<Accordion type='multiple' defaultValue={['identity', 'work']} className='space-y-6'>
					<AccordionItem value='identity'>
						<AccordionTrigger>{content.form.identity}</AccordionTrigger>
						<AccordionContent>
							<div className='space-y-4'>
								{field(
									content.form.fullName.value,
									<Input value={values.fullName} onChange={set('fullName')} autoFocus autoComplete='off' />,
									errors.fullName,
								)}
								{field(
									content.form.preferredName.value,
									<Input value={values.preferredName} onChange={set('preferredName')} autoComplete='off' />,
								)}
							</div>
						</AccordionContent>
					</AccordionItem>

					<AccordionItem value='work'>
						<AccordionTrigger>{content.form.work}</AccordionTrigger>
						<AccordionContent>
							<div className='space-y-4'>
								{field(
									content.form.email.value,
									<Input type='email' value={values.email} onChange={set('email')} autoComplete='off' />,
									errors.email,
								)}
								{field(
									content.form.position.value,
									<Input value={values.position} onChange={set('position')} autoComplete='off' />,
								)}
								{field(
									content.form.department.value,
									<Select
										value={values.departmentId || NO_DEPARTMENT}
										onValueChange={v => setSelect('departmentId')(v === NO_DEPARTMENT ? '' : v)}
									>
										<SelectTrigger>
											<SelectValue placeholder='-' />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value={NO_DEPARTMENT}>-</SelectItem>
											{departments.map(d => (
												<SelectItem key={d.id} value={d.id}>
													{d.name}
												</SelectItem>
											))}
										</SelectContent>
									</Select>,
								)}
								<div className='grid grid-cols-2 gap-4'>
									{field(
										content.form.employmentType.value,
										<Select value={values.employmentType} onValueChange={setSelect('employmentType')}>
											<SelectTrigger>
												<SelectValue />
											</SelectTrigger>
											<SelectContent>
												{EMPLOYMENT_TYPES.map(et => (
													<SelectItem key={et} value={et}>
														{content.employmentType[et]?.value ?? et}
													</SelectItem>
												))}
											</SelectContent>
										</Select>,
									)}
									{field(
										content.form.status.value,
										<Select value={values.status} onValueChange={setSelect('status')}>
											<SelectTrigger>
												<SelectValue />
											</SelectTrigger>
											<SelectContent>
												{STATUSES.map(s => (
													<SelectItem key={s} value={s}>
														{content.status[s]?.value ?? s}
													</SelectItem>
												))}
											</SelectContent>
										</Select>,
									)}
								</div>
								{field(
									content.form.startDate.value,
									<DatePicker
										value={values.startDate}
										onChange={v => setValues(prev => ({ ...prev, startDate: v }))}
									/>,
								)}
								{field(
									content.form.defaultWorkSchedule.value,
									<Input
										value={values.defaultWorkSchedule}
										onChange={set('defaultWorkSchedule')}
										placeholder='Seg-Sex 9-18'
										autoComplete='off'
									/>,
								)}
							</div>
						</AccordionContent>
					</AccordionItem>

					<AccordionItem value='contact'>
						<AccordionTrigger>{content.form.contact}</AccordionTrigger>
						<AccordionContent>
							<div className='space-y-4'>
								{field(
									content.form.workPhone.value,
									<Input type='tel' value={values.workPhone} onChange={set('workPhone')} autoComplete='off' />,
								)}
								{field(
									content.form.personalPhone.value,
									<Input type='tel' value={values.personalPhone} onChange={set('personalPhone')} autoComplete='off' />,
								)}
								{field(
									content.form.personalEmail.value,
									<Input
										type='email'
										value={values.personalEmail}
										onChange={set('personalEmail')}
										autoComplete='off'
									/>,
								)}
								{field(
									content.form.location.value,
									<Input
										value={values.location}
										onChange={set('location')}
										placeholder='São Paulo, BR'
										autoComplete='off'
									/>,
								)}
								{field(
									content.form.timezone.value,
									<Input
										value={values.timezone}
										onChange={set('timezone')}
										placeholder='America/Sao_Paulo'
										autoComplete='off'
									/>,
								)}
							</div>
						</AccordionContent>
					</AccordionItem>

					<AccordionItem value='address'>
						<AccordionTrigger>{content.form.address}</AccordionTrigger>
						<AccordionContent>
							<div className='space-y-4'>
								{field(
									content.form.addressLine1.value,
									<Input value={values.addressLine1} onChange={set('addressLine1')} autoComplete='off' />,
								)}
								{field(
									content.form.addressLine2.value,
									<Input value={values.addressLine2} onChange={set('addressLine2')} autoComplete='off' />,
								)}
								<div className='grid grid-cols-2 gap-4'>
									{field(
										content.form.countryCode.value,
										<Input
											value={values.countryCode}
											onChange={set('countryCode')}
											placeholder='BR'
											autoComplete='off'
										/>,
									)}
									{field(
										content.form.city.value,
										<Input value={values.city} onChange={set('city')} autoComplete='off' />,
									)}
								</div>
								<div className='grid grid-cols-2 gap-4'>
									{field(
										content.form.state.value,
										<Input value={values.state} onChange={set('state')} autoComplete='off' />,
									)}
									{field(content.form.zip.value, <Input value={values.zip} onChange={set('zip')} autoComplete='off' />)}
								</div>
							</div>
						</AccordionContent>
					</AccordionItem>
				</Accordion>
			</div>

			<div className='absolute bottom-0 left-0 right-0 p-4'>
				<div className='flex justify-end space-x-4'>
					<Button variant='outline' onClick={onCancel} type='button'>
						{content.form.cancel}
					</Button>
					<SubmitButton isSubmitting={submitting}>{isEdit ? content.form.update : content.form.create}</SubmitButton>
				</div>
			</div>
		</form>
	)
}

export { MemberForm }
export type { FormValues }
